/**
 * Remote error reporting for the web app (Sentry), OFF unless the build has
 * REACT_APP_SENTRY_DSN. errorTracking.js's reportError() - already the one
 * funnel every catch block uses - hands errors here; nothing else in the app
 * needs to know Sentry exists.
 *
 * What is reported:
 *   - unexpected failures: JavaScript exceptions, render errors (React
 *     error boundary), API 5xx answers, non-JSON answers;
 *   - by CATEGORY and a short stable context label ("document_upload",
 *     "meeting_end", "billing_checkout"), never the message or payload.
 * What is NOT an event (kept as a breadcrumb only): validation and
 * permission answers (4xx), signed-out (401), offline/network drops,
 * user-cancelled requests, WebSocket error events - all expected.
 *
 * Privacy: see monitoringScrub.js. No session replay, no screenshots, no
 * DOM text, no network bodies, no user identity. A monitoring or Sentry
 * failure never changes what the user sees.
 *
 * Never put METRICS_TOKEN or any server credential in a REACT_APP_
 * variable: everything REACT_APP_* is public in the bundle. The DSN is a
 * public ingestion key by design.
 */
import * as Sentry from '@sentry/react';

import {
  createRateLimiter,
  DROPPED_INTEGRATIONS,
  isCode,
  safeFields,
  scrubBreadcrumb,
  scrubEvent,
} from './monitoringScrub';

const DSN = (process.env.REACT_APP_SENTRY_DSN || '').trim();
const DSN_PATTERN = /^https:\/\/[0-9a-f]{16,64}@[a-z0-9.-]+(:\d+)?\/\d+$/;
const RELEASE = (process.env.REACT_APP_RELEASE || '').trim();
const ENVIRONMENT =
  process.env.REACT_APP_SENTRY_ENVIRONMENT ||
  process.env.REACT_APP_ENVIRONMENT ||
  process.env.NODE_ENV ||
  'development';

/** The same context+category is sent at most once per this long per tab. */
const REPORT_WINDOW_MS = 5 * 60 * 1000;
const allowReport = createRateLimiter(REPORT_WINDOW_MS);

let active = false;

export function monitoringActive() {
  return active;
}

/** Starts Sentry once, before the first render (index.js). Never throws. */
export function initMonitoring() {
  if (active) return true;
  if (!DSN || !DSN_PATTERN.test(DSN)) return false;
  try {
    Sentry.init({
      dsn: DSN,
      release: RELEASE ? `krishvaq-web@${RELEASE}` : undefined,
      environment: ENVIRONMENT,
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: false,
        httpBodies: [],
        urlQueryParams: false,
        graphQL: { document: false, variables: false },
        genAI: { inputs: false, outputs: false },
        stackFrameVariables: false,
      },
      sampleRate: 1,
      tracesSampleRate: 0,
      maxBreadcrumbs: 30,
      maxValueLength: 256,
      integrations: (defaults) => defaults.filter((integration) => !DROPPED_INTEGRATIONS.has(integration.name)),
      beforeSend: (event) => scrubEvent(event),
      beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb),
      // Browser-extension noise is not ours to fix.
      denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari-extension:\/\//i],
      ignoreErrors: ['ResizeObserver loop limit exceeded', 'ResizeObserver loop completed with undelivered notifications'],
    });
    active = true;
  } catch (_) {
    active = false;
  }
  return active;
}

/**
 * What kind of failure `error` is. Shapes come from services/httpClient.js
 * (status / isNetworkError / refreshUnavailable / code) and the browser.
 */
export function classifyError(error) {
  if (!error) return 'unknown';
  if (error.refreshUnavailable) return 'session_refresh_unavailable';
  if (error.isNetworkError && !error.status) return 'network';
  if (error.isNetworkError && error.status) return 'non_json_response';
  if (error.name === 'AbortError') return 'aborted';
  const status = Number(error.status);
  if (status === 401) return 'auth';
  if (status >= 400 && status < 500) return `client_${status}`;
  if (status >= 500 && status < 600) return [502, 503, 504].includes(status) ? `server_${status}` : 'server_5xx';
  if (typeof Event !== 'undefined' && error instanceof Event) return 'browser_event';
  if (error instanceof Error) return 'exception';
  return 'unknown';
}

/** Expected in normal use: recorded as breadcrumbs, never as events. */
const EXPECTED_CATEGORIES = new Set(['network', 'aborted', 'auth', 'session_refresh_unavailable', 'browser_event']);

function isExpected(category) {
  return EXPECTED_CATEGORIES.has(category) || category.startsWith('client_');
}

function contextLabel(context) {
  const text = String(context || 'unknown');
  return /^[a-z0-9_]{1,48}$/.test(text) ? text : 'other';
}

/**
 * Reports one error from reportError(). `extra` may carry allowlisted
 * fields only (componentStack from the error boundary, a stage). Never
 * throws, never changes the UI.
 */
export function captureReportedError(context, error, extra) {
  if (!active) return;
  try {
    const label = contextLabel(context);
    const category = classifyError(error);
    const fields = safeFields({
      context: label,
      category,
      status: Number(error && error.status) || undefined,
      code: isCode(error && error.code) ? error.code : undefined,
      ...(extra || {}),
    });

    if (isExpected(category)) {
      Sentry.addBreadcrumb({ category: 'krishvaq', message: 'UI_ERROR_EXPECTED', level: 'info', data: fields });
      return;
    }
    if (!allowReport(`${label}|${category}|${fields.code || ''}`)) return;

    Sentry.withScope((scope) => {
      scope.setTag('ui_context', label);
      scope.setTag('error_category', category);
      scope.setTag('error_kind', category === 'exception' ? 'defect' : 'operational');
      scope.setFingerprint(['ui', label, category, String(fields.code || '')]);
      if (fields.componentStack) {
        scope.setContext('react', { componentStack: fields.componentStack });
        delete fields.componentStack;
      }
      Object.keys(fields).forEach((key) => scope.setExtra(key, fields[key]));
      if (category === 'exception') {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(`UI_${category.toUpperCase()}`, 'error');
      }
    });
  } catch (_) {
    // Monitoring never affects the app.
  }
}
